/**
 * QuestionsSection — customer-facing Q&A for every stage that has
 * stage_questions, shown up front (enum order) so the customer can answer
 * ahead of the platform actually reaching that stage.
 */
import * as React from 'react';
import { toast } from '@finmin/ui';
import type { StageQuestionItem } from '@/api/services';
import { useSubmitStageAnswers } from '@/api/service-requests';
import { PALETTE_FOREST } from '../tokens';

const STAGE_ORDER = [
  'service_selected', 'docs_start', 'provider_assignment_start',
  'provider_assignment_complete', 'platform_clarification', 'draft_evaluation',
  'acknowledgement_submission', 'acknowledgement_submitted', 'service_request_complete',
];

const STAGE_LABELS: Record<string, string> = {
  service_selected: 'At intake',
  docs_start: 'Document upload',
  provider_assignment_start: 'Before provider assignment',
  provider_assignment_complete: 'Provider assignment complete',
  platform_clarification: 'Platform clarification',
  draft_evaluation: 'Before draft evaluation',
  acknowledgement_submission: 'Before acknowledgement',
  acknowledgement_submitted: 'Acknowledgement submitted',
  service_request_complete: 'Before completion',
};

export function QuestionsSection({
  serviceRequestId,
  questions,
  answers,
  readOnly,
}: {
  serviceRequestId: string;
  questions: StageQuestionItem[];
  answers: Record<string, Record<string, string>>;
  readOnly?: boolean;
}) {
  const submit = useSubmitStageAnswers();
  const [drafts, setDrafts] = React.useState<Record<string, Record<string, string>>>({});

  if (questions.length === 0) return null;

  const byStage = new Map<string, StageQuestionItem[]>();
  for (const q of questions) byStage.set(q.stage, [...(byStage.get(q.stage) ?? []), q]);
  const stages = STAGE_ORDER.filter((s) => byStage.has(s));

  function setDraft(stage: string, label: string, value: string) {
    setDrafts((prev) => ({ ...prev, [stage]: { ...prev[stage], [label]: value } }));
  }

  async function save(stage: string) {
    try {
      await submit.mutateAsync({
        id: serviceRequestId,
        stage,
        answers: drafts[stage] ?? {},
      });
      toast.success('Answers saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save answers');
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 space-y-3">
      {stages.map((stage) => (
        <div key={stage} className="rounded-2xl border border-border p-4 space-y-2">
          <p className="text-sm font-semibold" style={{ color: PALETTE_FOREST }}>
            {STAGE_LABELS[stage] ?? stage}
          </p>
          {byStage.get(stage)!.map((q) => {
            const current = drafts[stage]?.[q.label] ?? answers[stage]?.[q.label] ?? '';
            return (
              <div key={q.label} className="space-y-1">
                <label className="block text-xs font-medium text-foreground">
                  {q.label}
                  {q.is_essential && <span className="ml-0.5 text-destructive">*</span>}
                </label>
                {q.answer_type === 'text' && (
                  <input
                    type="text"
                    value={current}
                    onChange={(e) => setDraft(stage, q.label, e.target.value)}
                    disabled={readOnly}
                    className="h-9 w-full rounded-lg border border-border bg-background px-3 text-sm"
                  />
                )}
                {q.answer_type === 'multiple_choice' && (
                  <div className="flex flex-wrap gap-2">
                    {(q.options ?? []).map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setDraft(stage, q.label, opt)}
                        disabled={readOnly}
                        className="h-8 rounded-lg border px-3 text-xs"
                        style={{
                          borderColor: current === opt ? PALETTE_FOREST : undefined,
                          backgroundColor: current === opt ? `${PALETTE_FOREST}12` : undefined,
                        }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
                {q.answer_type === 'true_false' && (
                  <div className="flex gap-2">
                    {(['true', 'false'] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setDraft(stage, q.label, v)}
                        disabled={readOnly}
                        className="h-8 flex-1 rounded-lg border text-xs capitalize"
                        style={{
                          borderColor: current === v ? PALETTE_FOREST : undefined,
                          backgroundColor: current === v ? `${PALETTE_FOREST}12` : undefined,
                        }}
                      >
                        {v === 'true' ? 'Yes' : 'No'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => void save(stage)}
            disabled={submit.isPending || !drafts[stage] || readOnly}
            className="h-8 rounded-lg px-3 text-xs font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: PALETTE_FOREST }}
          >
            {submit.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      ))}
    </div>
  );
}
